import {
	ReactElement,
	useCallback
} from "react";
import { GenericNavigationPanelProps, SubNavigation } from "../SubNavigation";
import { localize } from "client_legacy/ndaw/culture";
import { AppType } from "client/Api/Apps/common";
import { ExchangeAvailability, useExchangeAvailability } from "client/Hooks";
import {
	SourceFilter,
	SourceOption,
	SourceSelector,
	SourceSelectorValue,
	useAvailableSourceOptions
} from "client/Features/AddressBook/SourceSelector";
import { getAddressBookUrl } from "client/Features/AddressBook/routing";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { useShouldStartLoadingData } from "./useShouldStartLoadingData";
import { AddressBookContactSource } from "client/Features/AddressBook/Overview/Overview";
import { isNotNil } from "client/Misc/utils";
import { ContactSource } from "client/Api/Email/contacts";
import { never } from "client_legacy/ndaw/errors";

type AddressBookPanelProps = GenericNavigationPanelProps;

export const AddressBookPanel = (props: AddressBookPanelProps): ReactElement | null => {
	const {
		isVisible,
		isActive,
		isPinned
	} = props;

	const exchangeAvailability: ExchangeAvailability = useExchangeAvailability();
	const availableSourceOptions: readonly SourceOption[] = useAvailableSourceOptions(exchangeAvailability);

	const shouldStartLoadingData: boolean = useShouldStartLoadingData(isPinned, isVisible);

	const isRenderedByPage: boolean = isActive;

	const handleNavigation = useCallback(
		(source: SourceSelectorValue): void => {
			navigateToAddressBookPage(
				source.source,
				source.filter
			);
		},
		[]
	);

	if (isRenderedByPage) {
		return null;
	}

	return (
		<>
			<SubNavigation.Header
				kind={AppType.AddressBook}
				title={localize("_AddressBook")}
			/>
			<SubNavigation.Body>
				{shouldStartLoadingData
					? (
						<SourceSelector
							options={availableSourceOptions}
							value={undefined}
							onChange={handleNavigation}
						/>
					)
					: (
						<SubNavigation.LoadingIndicator />
					)
				}
			</SubNavigation.Body>
		</>
	);
};

AddressBookPanel.displayName="AddressBookPanel";

export const navigateToAddressBookPage = (
	source: AddressBookContactSource | undefined,
	filter: SourceFilter | undefined
): void => {
	let url: string = getAddressBookUrl();

	if (isNotNil(source)) {
		switch (source) {
			case ContactSource.Gal:
			case ContactSource.Personal:
				url = getAddressBookUrl({ source });
				break;
			case ContactSource.Workspace:
				url = getAddressBookUrl({
					source,
					filter
				});
				break;
			default:
				never(source);
		}
	}

	navigateTo(url);
};